import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { useMerchants } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';
import { useGetPayoutsPausingStatus } from '../../../hooks/use-get-payouts-pausing-status';

export const ContactPayments = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });
  const { payoutsEnabled: payouts, chargesEnabled: charges, stripeId } = useMerchants(locationId)['firstLocation'];
  const { message: payoutsPausingMessage, payoutsPausingStatus } = useGetPayoutsPausingStatus(locationId);
  if (payouts && charges && payoutsPausingStatus !== 'error') {
    return null;
  }
  let message = '';
  let mailLink = '';

  if (payoutsPausingStatus === 'error') {
    message = payoutsPausingMessage;
  } else if (payouts && !charges) {
    message = t('Your Stripe account needs more verification and some payment features are temporarily paused.');
    mailLink = `mailto:payments@getweave.com?subject=${t('Inactive Payments on {{stripeId}}', { stripeId })}`;
  } else if (!payouts && charges) {
    message = t('You are currently unable to receive payouts to your bank account.');
    mailLink = `mailto:payments@getweave.com?subject=${t('Inactive Payouts on {{stripeId}}', { stripeId })}`;
  } else if (!payouts && !charges) {
    message = t(
      'Your Stripe account needs more verification. Payouts and some payment features are temporarily paused.'
    );
    mailLink = `mailto:payments@getweave.com?subject=${t('Inactive Payments and Payouts on {{stripeId}}', {
      stripeId,
    })}`;
  }

  return (
    <div
      css={css`
        padding: ${theme.spacing(1)};
        background-color: ${theme.colors.neutral5};
        margin-bottom: ${theme.spacing(2)};
        p {
          margin-bottom: ${theme.spacing(1)};
        }
        p:last-child {
          margin: 0;
        }
      `}
    >
      <Text size='medium'>
        <Trans t={t}>
          {{ message }} Please follow the steps outlined{' '}
          <TextLink
            to='https://www.weavehelp.com/weavehelp/article/stripe-troubleshooting'
            target='_blank'
            rel='noopener noreferrer'
          >
            here.
          </TextLink>
        </Trans>
      </Text>
      <Text size='medium'>
        <Trans t={t}>
          If you are still experiencing issues, contact{' '}
          <TextLink to={mailLink} rel='noopener noreferrer'>
            Weave Payments
          </TextLink>
          .
        </Trans>
      </Text>
    </div>
  );
};
